import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { CopilotRecipient, CopilotRecipients } from '../../../_interfaces/document';
import {
  ccRecipients,
  displayName,
  extraCcCount,
  hasPrimaryRecipient,
} from './recipient.helpers';

@Component({
  selector: 'app-recipient-fields',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, TranslateModule],
  templateUrl: './recipient-fields.component.html',
})
export class RecipientFieldsComponent {
  @Input() recipients?: CopilotRecipients;
  @Input() enabled = false;
  @Input() readOnly = false;

  @Output() toClick = new EventEmitter<void>();
  @Output() ccClick = new EventEmitter<void>();
  @Output() viewCc = new EventEmitter<void>();
  @Output() removePrimary = new EventEmitter<void>();
  @Output() removeCc = new EventEmitter<void>();

  get interactive(): boolean {
    return this.enabled && !this.readOnly;
  }

  get canOpenTo(): boolean {
    return this.interactive || (this.enabled && this.readOnly && this.hasPrimary);
  }

  get canOpenCc(): boolean {
    return this.interactive || (this.enabled && this.readOnly && this.hasCc);
  }

  get primary(): CopilotRecipient | null {
    return this.recipients?.primaryRecipient ?? null;
  }

  get hasPrimary(): boolean {
    return hasPrimaryRecipient(this.recipients);
  }

  get ccList(): CopilotRecipient[] {
    return ccRecipients(this.recipients);
  }

  get firstCc(): CopilotRecipient | null {
    return this.ccList[0] ?? null;
  }

  get hasCc(): boolean {
    return this.ccList.length > 0;
  }

  get overflowCount(): number {
    return extraCcCount(this.recipients);
  }

  displayName = displayName;

  onToClick(): void {
    if (!this.canOpenTo) return;
    this.toClick.emit();
  }

  onToKeydown(event: KeyboardEvent): void {
    if (!this.isFieldActivation(event)) return;
    this.onToClick();
  }

  onCcClick(): void {
    if (!this.canOpenCc) return;
    this.ccClick.emit();
  }

  onCcKeydown(event: KeyboardEvent): void {
    if (!this.isFieldActivation(event)) return;
    this.onCcClick();
  }

  private isFieldActivation(event: KeyboardEvent): boolean {
    if (event.target !== event.currentTarget) return false;
    if (event.key !== 'Enter' && event.key !== ' ') return false;
    event.preventDefault();
    return true;
  }

  onRemovePrimary(event: Event): void {
    event.stopPropagation();
    if (!this.interactive) return;
    this.removePrimary.emit();
  }

  onRemoveCc(event: Event): void {
    event.stopPropagation();
    if (!this.interactive) return;
    this.removeCc.emit();
  }

  onViewCc(event: Event): void {
    event.stopPropagation();
    if (!this.enabled) return;
    this.viewCc.emit();
  }
}
